import {Component, effect, input, InputSignal, output, signal} from '@angular/core';
import {EvenementService} from '../../shared/services/evenement.service';
import {finalize} from 'rxjs/operators';
import {AlertService, DateUtils, isBlank, isNullOrUndefined} from 'sigale-angular';
import {DateUtilsService} from '../../shared/services/date-utils.service';
import {StatutEvenement} from '../../shared/models/statut-evenement.model';
import {TranslateService} from '@ngx-translate/core';
import {Evenement} from '../../shared/components/calendrier/model/evenement.model';
import {TypeEvenement} from '../../shared/models/type-evenement.model';

@Component({
	selector: 'app-detail-evenement-modal',
	templateUrl: './detail-evenement-modal.component.html',
	styleUrl: './detail-evenement-modal.component.scss'
})
export class DetailEvenementModalComponent {

	readonly NOM_ALERTE_DETAIl_EVENEMENT_MODAL = 'alertDetailEvenementModal';

	visible: InputSignal<boolean> = input.required<boolean>();
	evenement = input.required<Evenement>();
	langueUtilisateur = input.required<string>();

	visibleChange = output<boolean>();
	notificationEvenementAnnule = output<Evenement>();

	loadingAnnulation = signal<boolean>(false);
	visibleLocal = signal(false);

	constructor(
		private evenementService: EvenementService,
		private alertService: AlertService,
		private dateUtilsService: DateUtilsService,
		private translateService: TranslateService) {
		// A chaque modification de visible qui est renseigné par le parent (ex à l'ouverture de la modale) on notifie modifie le visible local
		effect(() => {
			this.visibleLocal.set(this.visible());
		}, {allowSignalWrites: true});

		// Chaque fois que le visible en local est modifié on emit la valeur au parent.
		effect(() => {
			const val = this.visibleLocal();
			this.visibleChange.emit(val);
		});
	}

	/**
	 * Formatte la date pour l'affichage.
	 * @param date la date à formatter.
	 */
	formattageDateSansHeurePourAffichage(date: Date) {
		return this.dateUtilsService.formaterDateEnFormatLettre(new Date(date), this.langueUtilisateur());
	}

	/**
	 * Formatte la date pour l'affichage.
	 * @param date la date à formatter.
	 */
	formattageDateHeurePourAffichage(date: Date) {
		return this.dateUtilsService.formaterDateHeureEnFormatLettre(new Date(date), this.langueUtilisateur());
	}

	/**
	 * Si le statut est VALIDE alors le label sera Validé le sinon Refusé le.
	 */
	messageValideLeOuRefuseLe() {
		return this.evenement().statut.code === StatutEvenement.VALIDE ?
			this.translateService.instant('ACCUEIL.DETAIL_EVENEMENT.VALIDE_LE') :
			this.translateService.instant('ACCUEIL.DETAIL_EVENEMENT.REFUSE_LE');
	}

	/**
	 * Annule l'évènement.
	 */
	annulerEvenement(): void {
		this.loadingAnnulation.set(true);
		this.evenementService.annulerEvenement(this.evenement().id)
			.pipe(finalize(() => this.loadingAnnulation.set(false)))
			.subscribe({
				next: () => {
					this.notificationEvenementAnnule.emit(this.evenement());
					this.fermerModal();
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_DETAIl_EVENEMENT_MODAL);
					this.alertService.error(error.error, this.NOM_ALERTE_DETAIl_EVENEMENT_MODAL);
				}
			});
	}

	/**
	 * Effectue les actions de fermeture de modal
	 */
	fermerModal(): void {
		this.visibleChange.emit(false);
		this.visibleLocal.set(false);
		this.alertService.clear(this.NOM_ALERTE_DETAIl_EVENEMENT_MODAL);
	}

	/**
	 * Renvoi la classe selon le statut.
	 * @param statut le statut.
	 */
	determinerClasseStatut(statut: string): string {
		switch (statut as StatutEvenement) {
			case StatutEvenement.EN_ATTENTE:
				return `statut-${StatutEvenement.EN_ATTENTE}`;
			case StatutEvenement.VALIDE:
				return `statut-${StatutEvenement.VALIDE}`;
			case StatutEvenement.REFUSE:
				return `statut-${StatutEvenement.REFUSE}`;
		}
	}

	/**
	 * Vrai si le statut n'est pas annulé ou refusé et que la date d'évènement est supérieure ou égale à aujourd'hui, faux sinon.
	 */
	estAfficherBoutonAnnulation(): boolean {
		return this.evenement().statut.code !== StatutEvenement.REFUSE
			&& !DateUtils.isDayBefore(this.evenement().date, new Date());
	}

	/**
	 * True si l'évènement est un jour de télétravail, False sinon.
	 */
	estEvenementTeletravail(): boolean {
		return this.evenement().estTeletravail();
	}

	/**
	 * On affiche les informations de validation si c'est un jour de télétravail et si le statut a été validé ou refusé.
	 */
	afficherInformationsValidation(): boolean {
		return this.estEvenementTeletravail() && this.evenement().statut.code !== StatutEvenement.EN_ATTENTE;
	}

	/**
	 * Renvoie le bon icône selon le type d'évènement
	 */
	iconeSelonTypeEvenement(): string {
		if (this.estEvenementTeletravail()) {
			return 'pi pi-home';
		}

		return this.evenement().typeEvenement.code === TypeEvenement.CONGE ? 'pi pi-sun' : 'pi pi-globe';
	}

	protected readonly isNullOrUndefined = isNullOrUndefined;
	protected readonly StatutEvenement = StatutEvenement;
	protected readonly isBlank = isBlank;
}
